import { createAsyncThunk } from "@reduxjs/toolkit";
import { selectAllQuestionsByFormType } from "../storage/slices";
import {
  ADDRESS_SUB_TYPE_VALUES,
  HTML_FORM_CONSTANTS,
  SUPPORTED_INPUT_TYPES,
} from "../../Forms/util/constants";
import { sliceNames } from "../util/constants";
import { isValid } from "date-fns";
import { FORM_STATUSES } from "../../util/constants";
import { ROUTE_KEYS, ROUTES } from "../../_configuration/routing/constants";
import { createTagHash, formApi } from "./api";
import {
  showErrorAlert,
  showInfoAlert,
  showSuccessAlert,
} from "../alert/thunks";
import { post } from "../http/functions";

const supportedActionIntents = HTML_FORM_CONSTANTS.INTENTS;
const name = sliceNames.form;

const buildNestedObject = (obj, path, value) => {
  const keys = path.split(".");
  let current = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (!current[key]) current[key] = {};
    current = current[key];
  }
  const lastKey = keys[keys.length - 1];
  current[lastKey] = value;

  return obj;
};

export const formatPostBody = ({ body, getState, formType, formId }) => {
  const questions = selectAllQuestionsByFormType(getState(), {
    formType,
    formId,
  });
  let missingFields = [];
  let misformattedFields = [];
  let dynamoPayload = {
    type: formType,
  };
  let dataFields = [];
  questions.forEach(({ dataType = "", required, choices, ...rest }) => {
    let dtUC = dataType.toUpperCase();
    const dataField = rest?.dataField || rest?.name;
    if (dtUC === SUPPORTED_INPUT_TYPES.address) {
      dataFields = dataFields.concat(
        Object.values(ADDRESS_SUB_TYPE_VALUES).map((val) => {
          return {
            dataField: dataField + "." + val,
            dataType: dtUC,
          };
        })
      );
    } else {
      dataFields.push({
        dataField,
        required,
        dataType: dtUC,
      });
    }
    if (Array.isArray(choices) && choices?.length) {
      const additionalQuestions = choices.flatMap((choice) => {
        const relatedQuestions = choice?.relatedQuestions || [];
        return relatedQuestions.map((rq) => ({
          dataField: rq?.dataField || rq?.name,
          required: rq?.required,
          dataType: rq?.dataType,
        }));
      });

      dataFields = dataFields.concat(additionalQuestions);
    }
  });

  dataFields.forEach(({ dataField, dataType, required, ...rest }) => {
    let relatedValue = body?.[dataField];
    if (dataType === SUPPORTED_INPUT_TYPES.datepicker) {
      const _date = new Date(relatedValue);
      if (!relatedValue && required) {
        missingFields.push(dataField);
      } else if (isValid(_date)) {
        relatedValue = _date.toISOString();
      } else if (relatedValue) {
        misformattedFields.push(dataField);
      }
    }
    const isMap = dataField?.includes(".");
    if (isMap) {
      buildNestedObject(dynamoPayload, dataField, relatedValue);
    } else {
      dynamoPayload[dataField] = relatedValue;
    }
    if (required && !relatedValue) {
      missingFields.push(dataField);
    }
  });

  return {
    dynamoPayload,
    missingFields,
    misformattedFields,
  };
};

export const createForm = createAsyncThunk(
  `${name}/create`,
  async ({ body, formType, status }, { getState, rejectWithValue }) => {
    try {
      const { missingFields, dynamoPayload } = formatPostBody({
        body,
        getState,
        formType,
      });
      let path = "/forms";
      if (status) {
        path += `?status=${status}`;
      }
      const { data: response } = await post(
        path,
        dynamoPayload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      const { sk = "", formType: resFType, id } = response;
      let res = {
        ...response,
        missingFields,
        formType: resFType,
        id,
      };
      const [_formType, _uuid] = sk?.split("#") || [];
      if (!res.formType) {
        res.formType = _formType;
      }
      if (!res.id) {
        res.id = _uuid;
      }
      res.formType = res.formType.toLowerCase();
      return res;
    } catch (error) {
      return rejectWithValue(error?.message);
    }
  }
);

export const editForm = createAsyncThunk(
  `${name}/edit`,
  async (
    { body, formType, identifier, status },
    { getState, rejectWithValue }
  ) => {
    try {
      let { missingFields, dynamoPayload } = formatPostBody({
        body,
        getState,
        formType,
        formId: identifier,
      });
      let path = `/forms/${formType}/${identifier}`;
      if (status) {
        path += `?status=${status}`;
      }

      const { data: response } = await post(
        path,
        dynamoPayload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      const { sk = "", formType: resFType, id } = response;
      let res = {
        ...response,
        missingFields,
        formType: resFType,
        id,
      };
      const [_formType, _uuid] = sk?.split("#") || [];
      if (!res.formType) {
        res.formType = _formType;
      }
      if (!res.id) {
        res.id = _uuid;
      }
      res.formType = res.formType.toLowerCase();
      return res;
    } catch (error) {
      return rejectWithValue(error?.message);
    }
  }
);

export const updateFormStatus = createAsyncThunk(
  `${name}/updateStatus`,
  async (
    { formType, identifier, status, body },
    { getState, rejectWithValue }
  ) => {
    try {
      let path = `/forms/${formType}/${identifier}?status=${status}`;
      const options = {
        headers: {
          "Content-Type": "application/json",
        },
      };
      let postBody
      if (body) {
        let { dynamoPayload } = formatPostBody({
          body,
          getState,
          formType,
          formId: identifier,
        });
        postBody = dynamoPayload;
      }
      const { data: response } = await post(
        path,
        postBody,
        options
      );
      const { sk = "", formType: resFType, id } = response;
      const [_formType, _uuid] = sk?.split("#") || [];
      const apiFormType = (formType = resFType || _formType);
      const apiUUID = id || _uuid;
      if (apiFormType && apiUUID) {
        return {
          ...response,
          formType: apiFormType,
          id: apiUUID,
        };
      } else {
        throw new Error("Unexpected response from status update");
      }
    } catch (error) {
      return rejectWithValue(error?.message);
    }
  }
);

const clearCache = ({ formId, formType }) => {
  if (formId) {
    return formApi.util.invalidateTags([
      {
        type: "Form",
        id: createTagHash({
          formType,
          id: formId,
        }),
      },
    ]);
  }
};

const actionTitles = HTML_FORM_CONSTANTS.TITLES;

const createAlertMessage = ({ action, formStatus, ...rest }) => {
  const title = actionTitles[action] || action;
  const status = Object.values(FORM_STATUSES).find((_s) => {
    return _s.toUpperCase() === formStatus.toUpperCase();
  });
  let message = "";
  if (status) {
    message += `This form has a status of ${status}.`;
    if (status === FORM_STATUSES.DRAFT) {
      message += ` Please make sure to submit before the reporting period ends.`;
    }
  }
  return {
    title,
    message,
    status,
  };
};

export const dispatchFormEvent = createAsyncThunk(
  `${name}/dispatcher`,
  async (params = {}, { dispatch, rejectWithValue, fulfillWithValue }) => {
    const { formId, formType, body = {}, action } = params;
    try {
      const redirectPathBase = `${ROUTES[ROUTE_KEYS.FORMS].path}/${formType}`;
      const cacheCallback = () => {
        if (formId && formType) {
          dispatch(
            clearCache({
              formId,
              formType,
            })
          );
        }
      };

      const fulfill = (payload = {}) => {
        const { title, message, status } = createAlertMessage({
          ...payload,
          action,
        });
        const actionHandler = [
          FORM_STATUSES.SUBMITTED,
          FORM_STATUSES.REPORTED,
          FORM_STATUSES.REPORTED_ED_1,
          FORM_STATUSES.REPORTED_ED_2,
        ].includes(status)
          ? showSuccessAlert
          : showInfoAlert;
        dispatch(actionHandler(message, title));
        return fulfillWithValue(payload);
      };

      if (action === supportedActionIntents.accept) {
        if (!formId) {
          return rejectWithValue(
            `Accept can not be performed since no id was created`
          );
        }
        const res = await dispatch(
          updateFormStatus({
            formType,
            identifier: formId,
            body,
            status: FORM_STATUSES.REPORTED,
          })
        );
        const confirmedFormId = res?.payload?.id;
        if (confirmedFormId) {
          cacheCallback();
          return fulfill({
            redirectPath: `${redirectPathBase}/${confirmedFormId}`,
            ...res.payload,
          });
        }
      }
      if (action === supportedActionIntents.delete) {
        if (!formId) {
          return rejectWithValue(
            `Delete can not be performed since no id was created`
          );
        }
        const res = await dispatch(
          updateFormStatus({
            formType,
            identifier: formId,
            status: FORM_STATUSES.DELETED,
          })
        );
        cacheCallback();
        const confirmedFormId = res?.payload?.id;
        if (confirmedFormId) {
          cacheCallback();
          return fulfill({
            redirectPath: `${redirectPathBase}/${confirmedFormId}`,
            ...res.payload,
          });
        }
      }
      const submitOrSave =
        action === supportedActionIntents.submit ||
        action === supportedActionIntents.save;
      const useCreateForm = !formId && submitOrSave;
      const useEditForm = formId && submitOrSave;
      const status =
        action === supportedActionIntents.submit
          ? FORM_STATUSES.SUBMITTED
          : undefined;

      if (useEditForm) {
        const res = await dispatch(
          editForm({
            body,
            formType,
            status,
            identifier: formId,
          })
        );
        const confirmedFormId = res?.payload?.id;
        if (confirmedFormId) {
          cacheCallback();
          return fulfill({
            redirectPath: `${redirectPathBase}/${confirmedFormId}`,
            ...res.payload,
          });
        }
      } else if (useCreateForm) {
        const res = await dispatch(
          createForm({
            body,
            formType,
            status,
          })
        );
        const newFormId = res?.payload?.id;
        if (newFormId) {
          cacheCallback();
          return fulfill({
            redirectPath: `${redirectPathBase}/${newFormId}`,
            ...res.payload,
          });
        }
      }
      throw new Error(
        `Reached the end of the form event dispatcher with no matching call for action: ${action}`
      );
    } catch (error) {
      let message = action
        ? `There was an error while attempting to ${action} your form.`
        : `An error occured.`;
      message += ` Please review the form and retry.`;
      let title = `Error`;
      if (action) title += ` on ${action}`;
      dispatch(showErrorAlert(message, title));
      return rejectWithValue(error?.message || error);
    }
  }
);